import React, { useEffect, useMemo, useState } from 'react'
import { CAlert, CBadge, CButton, CCol, CForm, CFormInput, CFormLabel, CFormSelect, CRow, CSpinner } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../shared/icons'
import { useUI } from '../../shared/ui/UIProvider'
import { request } from '../../services'
import ScriptConfigModal from './ScriptConfigModal'
import DashboardPermissionsPanel from './DashboardPermissionsPanel'

function AgencyDetailPanel({ agency, open }) {
  const { toast } = useUI()
  const parentPcc = agency.parent_pcc
  const [pccs, setPccs] = useState([])
  const [users, setUsers] = useState([])
  const [catalog, setCatalog] = useState([])
  const [filterPcc, setFilterPcc] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [pccForm, setPccForm] = useState({ pcc: '' })
  const [userForm, setUserForm] = useState({ username: '', pcc: '' })
  const [localPccs, setLocalPccs] = useState([])

  // El backend solo lista PCCs que ya tienen scripts o usuarios. Para que un PCC
  // recien creado se pueda usar al instante (crear usuario), lo fusionamos aca.
  const mergedPccs = useMemo(() => {
    const byPcc = new Map()
    pccs.forEach((pcc) => byPcc.set(pcc.pcc, pcc))
    localPccs.forEach((code) => {
      if (!byPcc.has(code)) byPcc.set(code, { pcc: code, parent_pcc: parentPcc, users: 0, scripts: 0, isNew: true })
    })
    return Array.from(byPcc.values()).sort((a, b) => a.pcc.localeCompare(b.pcc))
  }, [pccs, localPccs, parentPcc])

  async function load() {
    if (!parentPcc) return
    setLoading(true)
    setMessage('')
    const [pccRes, userRes, catalogRes] = await Promise.all([
      request('agency_pccs_list', { params: { parent_pcc: parentPcc } }),
      request('agency_users_list', { params: { parent_pcc: parentPcc } }),
      request('scripts_catalog'),
    ])
    setLoading(false)
    if (!pccRes.ok) {
      setMessage(pccRes.data?.error || 'No se pudieron cargar los PCCs')
      return
    }
    setPccs(Array.isArray(pccRes.data) ? pccRes.data : [])
    setUsers(userRes.ok && Array.isArray(userRes.data) ? userRes.data : [])
    setCatalog(catalogRes.ok && Array.isArray(catalogRes.data) ? catalogRes.data : [])
  }

  useEffect(() => {
    if (open) load()
  }, [open, parentPcc])

  const visibleUsers = useMemo(() => {
    return !filterPcc ? users : users.filter((user) => String(user.pcc).toUpperCase() === filterPcc)
  }, [users, filterPcc])

  async function createPcc(event) {
    event.preventDefault()
    const pcc = pccForm.pcc.trim().toUpperCase()
    if (!/^[A-Z0-9]{3,4}$/.test(pcc)) {
      setMessage('PCC invalido')
      return
    }
    const res = await request('agency_pcc_create', { method: 'POST', body: { parent_pcc: parentPcc, pcc } })
    if (!res.ok) {
      const detail = res.data?.error || 'No se pudo crear el PCC'
      setMessage(detail)
      toast(detail, 'danger')
      return
    }
    setPccForm({ pcc: '' })
    setLocalPccs((current) => (current.includes(pcc) ? current : [...current, pcc]))
    setUserForm((current) => ({ ...current, pcc }))
    toast(`PCC ${pcc} agregado. Ya podes crear usuarios en el.`, 'success')
    load()
  }

  async function createUser(event) {
    event.preventDefault()
    const username = userForm.username.trim()
    const pcc = userForm.pcc.trim().toUpperCase()
    if (!/^[0-9]{1,20}$/.test(username) || !/^[A-Z0-9]{3,4}$/.test(pcc)) {
      setMessage('Usuario o PCC invalido')
      return
    }
    const res = await request('agency_user_create', {
      method: 'POST',
      body: {
        parent_pcc: parentPcc,
        pcc,
        username,
        agency: {
          name: agency.name,
          description: agency.description,
          status: agency.status,
          payment_plan: agency.payment_plan || null,
        },
      },
    })
    if (!res.ok) {
      const detail = res.data?.error || 'No se pudo crear el usuario'
      setMessage(detail)
      toast(detail, 'danger')
      return
    }
    setUserForm({ username: '', pcc: '' })
    setFilterPcc(pcc)
    toast(`Usuario ${username} creado en ${pcc}`, 'success')
    load()
  }

  if (!open) return null

  return (
    <div className="agency-panel">
      {loading ? <div className="agency-loading"><CSpinner color="success" /> Cargando PCCs y usuarios</div> : null}
      {message ? <CAlert color="warning" onClose={() => setMessage('')} dismissible>{message}</CAlert> : null}
      <div className="agency-panel-grid">
        <section className="agency-mini-card">
          <h4><CIcon icon={icon('cilSitemap')} /> PCCs dependientes</h4>
          <CForm className="agency-inline-form" onSubmit={createPcc}>
            <div>
              <CFormLabel>Nuevo PCC</CFormLabel>
              <CFormInput value={pccForm.pcc} maxLength={4} placeholder="E2LC" onChange={(event) => setPccForm({ pcc: event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} />
            </div>
            <CButton color="success" className="btn-brand" type="submit"><CIcon icon={icon('cilPlus')} /> Agregar</CButton>
          </CForm>
          <p className="agency-hint">Se vincula al Parent PCC {parentPcc} y queda disponible para usuarios.</p>
          <div className="agency-mini-list">
            {mergedPccs.length === 0 ? <div className="empty compact">Todavia no hay PCCs cargados</div> : mergedPccs.map((pcc) => (
              <div className="agency-mini-item" key={pcc.pcc}>
                <strong>{pcc.pcc}{pcc.isNew ? <CBadge color="success" className="ms-2">nuevo</CBadge> : null}</strong>
                <div className="agency-mini-meta">
                  <span>{Number(pcc.users || 0)} usuarios</span>
                  <span>{Number(pcc.scripts || 0)} scripts</span>
                </div>
                <ScriptAssignmentList
                  kind="pcc"
                  target={{ parent_pcc: parentPcc, pcc: pcc.pcc }}
                  catalog={catalog}
                  onChanged={load}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="agency-mini-card">
          <h4><CIcon icon={icon('cilPeople')} /> Usuarios de la agencia</h4>
          <CForm className="agency-inline-form two-cols" onSubmit={createUser}>
            <div>
              <CFormLabel>Usuario SABRE</CFormLabel>
              <CFormInput value={userForm.username} maxLength={20} placeholder="2706" onChange={(event) => setUserForm({ ...userForm, username: event.target.value.replace(/\D/g, '') })} />
            </div>
            <div>
              <CFormLabel>PCC</CFormLabel>
              <CFormSelect value={userForm.pcc} disabled={mergedPccs.length === 0} onChange={(event) => setUserForm({ ...userForm, pcc: event.target.value })}>
                <option value="">{mergedPccs.length ? 'Selecciona un PCC' : 'Primero crea un PCC'}</option>
                {mergedPccs.map((pcc) => <option key={pcc.pcc} value={pcc.pcc}>{pcc.pcc}</option>)}
              </CFormSelect>
            </div>
            <CButton color="success" className="btn-brand" type="submit" disabled={mergedPccs.length === 0}><CIcon icon={icon('cilUserPlus')} /> Crear</CButton>
          </CForm>
          <CRow className="g-2 mb-3">
            <CCol>
              <CFormLabel>Filtrar por PCC</CFormLabel>
              <CFormSelect value={filterPcc} onChange={(event) => setFilterPcc(event.target.value)}>
                <option value="">Todos los PCCs</option>
                {mergedPccs.map((pcc) => <option key={pcc.pcc} value={pcc.pcc}>{pcc.pcc}</option>)}
              </CFormSelect>
            </CCol>
            <CCol xs="auto" className="align-self-end">
              <CButton color="light" onClick={load}><CIcon icon={icon('cilReload')} /></CButton>
            </CCol>
          </CRow>
          <div className="agency-mini-list">
            {visibleUsers.length === 0 ? <div className="empty compact">No hay usuarios para mostrar</div> : visibleUsers.map((user) => (
              <div className="agency-mini-item" key={`${user.username}-${user.pcc}`}>
                <strong>{user.username}</strong>
                <div className="agency-mini-meta">
                  {user.name ? <span>{user.name}</span> : null}
                  <span className="mono">{user.parent_pcc || parentPcc} / {user.pcc}</span>
                  {user.token ? <span>con token</span> : null}
                </div>
                <ScriptAssignmentList
                  kind="user"
                  target={{ username: user.username, parent_pcc: user.parent_pcc || parentPcc, pcc: user.pcc }}
                  catalog={catalog}
                  onChanged={load}
                />
              </div>
            ))}
          </div>
        </section>

        <DashboardPermissionsPanel parentPcc={parentPcc} pccs={mergedPccs} users={users} />
      </div>
    </div>
  )
}

function ScriptAssignmentList({ kind, target, catalog, onChanged }) {
  const { toast, confirm } = useUI()
  const [scripts, setScripts] = useState([])
  const [uuid, setUuid] = useState('')
  const [loading, setLoading] = useState(false)
  const [configScript, setConfigScript] = useState(null)

  async function loadScripts() {
    setLoading(true)
    const action = kind === 'pcc' ? 'pcc_scripts' : 'user_scripts'
    const res = await request(action, { params: target })
    setLoading(false)
    setScripts(res.ok && Array.isArray(res.data) ? res.data : [])
  }

  useEffect(() => {
    loadScripts()
  }, [kind, target.username, target.parent_pcc, target.pcc])

  const scopeLabel = kind === 'pcc' ? `PCC ${target.pcc}` : `usuario ${target.username}`

  async function assign(event) {
    event.preventDefault()
    const selected = catalog.find((script) => script.uuid === uuid)
    if (!selected) return
    const action = kind === 'pcc' ? 'assign_script_pcc' : 'assign_script'
    const res = await request(action, {
      method: 'POST',
      body: {
        ...target,
        uuid,
        apply_config: '0',
        config: '',
      },
    })
    if (res.ok) {
      setUuid('')
      toast(`Script "${selected.name}" asignado al ${scopeLabel}`, 'success')
      await loadScripts()
      if (onChanged) onChanged()
    } else {
      toast(res.data?.error || 'No se pudo asignar el script', 'danger')
    }
  }

  async function toggle(script) {
    const action = kind === 'pcc' ? 'toggle_script_status_pcc' : 'toggle_script_status'
    const res = await request(action, { method: 'POST', body: { ...target, uuid: script.uuid } })
    if (res.ok) {
      toast(`Script "${script.name || script.uuid}" ${script.status === 1 ? 'desactivado' : 'activado'}`, 'info')
      loadScripts()
    } else {
      toast(res.data?.error || 'No se pudo cambiar el estado del script', 'danger')
    }
  }

  async function remove(script) {
    const confirmed = await confirm({
      title: 'Quitar script',
      body: `Vas a desasignar "${script.name || script.uuid}" del ${scopeLabel}.`,
      confirmText: 'Quitar',
      cancelText: 'Cancelar',
      tone: 'danger',
      icon: 'cilTrash',
    })
    if (!confirmed) return
    const action = kind === 'pcc' ? 'unassign_script_pcc' : 'unassign_script'
    const res = await request(action, { method: 'POST', body: { ...target, uuid: script.uuid } })
    if (res.ok) {
      toast(`Script "${script.name || script.uuid}" desasignado`, 'success')
      loadScripts()
    } else {
      toast(res.data?.error || 'No se pudo desasignar el script', 'danger')
    }
  }

  return (
    <div className="assignment-inline">
      <CForm className="assignment-form" onSubmit={assign}>
        <CFormSelect size="sm" value={uuid} onChange={(event) => setUuid(event.target.value)}>
          <option value="">Asignar script</option>
          {catalog.map((script) => <option key={script.uuid} value={script.uuid}>{script.name}</option>)}
        </CFormSelect>
        <CButton color="success" size="sm" className="btn-brand" type="submit" disabled={!uuid}>Asignar</CButton>
      </CForm>
      <div className="script-chip-list">
        {loading ? <span className="empty-scripts">Cargando scripts...</span> : null}
        {!loading && scripts.length === 0 ? <span className="empty-scripts">Sin scripts asignados</span> : scripts.map((script) => (
          <span key={`${script.uuid}-${script.foundinpcc}`} className={`script-chip ${script.status === 1 ? '' : 'inactive'} ${Number(script.foundinpcc || 0) === 1 ? 'inherited' : ''}`}>
            {script.name || script.uuid}
            <span className="scope">{Number(script.foundinpcc || 0) === 1 ? 'PCC' : kind.toUpperCase()}</span>
            {kind === 'user' && Number(script.foundinpcc || 0) === 1 ? null : (
              <>
                <button type="button" className="x" title="Editar configuracion" onClick={() => setConfigScript(script)}>
                  <CIcon icon={icon('cilSettings')} />
                </button>
                <button type="button" className="x" title={script.status === 1 ? 'Desactivar' : 'Activar'} onClick={() => toggle(script)}>
                  <CIcon icon={icon(script.status === 1 ? 'cilToggleOn' : 'cilToggleOff')} />
                </button>
                <button type="button" className="x" title="Eliminar" onClick={() => remove(script)}>x</button>
              </>
            )}
          </span>
        ))}
      </div>
      {configScript ? (
        <ScriptConfigModal
          open={Boolean(configScript)}
          kind={kind}
          target={target}
          script={configScript}
          onClose={() => setConfigScript(null)}
          onSaved={loadScripts}
        />
      ) : null}
    </div>
  )
}

export default AgencyDetailPanel
